import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { toast } from 'react-hot-toast';
import { saveReport } from '~/services/reports';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    jsonFields: string;
    onSaved?: () => void;
}

const SaveSearchModal: React.FC<Props> = ({ isOpen, onClose, jsonFields, onSaved }) => {
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [visibleToAll, setVisibleToAll] = useState(true);
    const [saving, setSaving] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setName('');
            setNotes('');
            setVisibleToAll(true);
            setNameError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const keyHandler = ({ key }: KeyboardEvent) => {
            if (key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
            return () => document.removeEventListener('keydown', keyHandler);
        }
    }, [isOpen, onClose]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('Search name is required.');
            return;
        }
        setNameError(null);
        setSaving(true);
        try {
            await saveReport({
                name: trimmed,
                notes: notes.trim() || undefined,
                visible_to_all_users: visibleToAll,
                json_fields: jsonFields,
            });
            toast.success('Search saved.');
            onSaved?.();
            onClose();
        } catch (err: any) {
            const fieldErrors = err?.data?.name;
            if (Array.isArray(fieldErrors) && fieldErrors.length) {
                setNameError('A saved search with this name already exists.');
            } else {
                toast.error(err?.message || 'Failed to save search.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
            >
                <button
                    type='button'
                    className='text-gray-600 hover:text-gray-900 absolute right-4 top-4'
                    onClick={onClose}
                    aria-label='Close'
                >
                    <RxCross2 size={20} />
                </button>
                <div className='p-8'>
                    <h2 className='mb-6 text-[22px] font-bold'>Save Search</h2>
                    <form onSubmit={handleSubmit}>
                        <div className='mb-4'>
                            <label className='block text-sm font-semibold mb-1'>
                                Search Name <span className='text-red-500'>*</span>
                            </label>
                            <input
                                type='text'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className='w-full rounded border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none'
                                autoFocus
                            />
                            <p className='mt-1 text-xs text-gray-500'>
                                This is how you will find this search again in the future
                            </p>
                            {nameError && (
                                <p className='mt-1 text-xs text-red-500'>{nameError}</p>
                            )}
                        </div>

                        <div className='mb-6'>
                            <label className='block text-sm font-semibold mb-1'>Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={4}
                                className='w-full rounded border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none'
                            />
                        </div>

                        <div className='mb-6'>
                            <div className='text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2'>
                                Permissions
                            </div>
                            <label className='flex items-center gap-2 cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={visibleToAll}
                                    onChange={(e) => setVisibleToAll(e.target.checked)}
                                    className='rounded border-gray-300 text-primary focus:ring-primary'
                                />
                                <span className='text-sm text-gray-700'>Visible to all users</span>
                            </label>
                        </div>

                        <div className='flex justify-start gap-3'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                disabled={saving}
                                className='rounded-lg bg-primary px-6 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50'
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SaveSearchModal;
